<script setup lang="ts">
import type { NuxtError } from '#app'

const props = defineProps<{ error: NuxtError }>()
const router = useRouter()
const seconds = ref(3)
let timer: ReturnType<typeof setInterval> | undefined

const isNotFound = computed(() => props.error.statusCode === 404)
const title = computed(() => isNotFound.value ? '文章不存在' : '页面暂时不可用')
const description = computed(() => isNotFound.value
  ? '这篇文章可能已删除、尚未发布，或链接地址不正确。'
  : '请求未能完成，请稍后重试。')

function goBack() {
  if (import.meta.client && window.history.state?.back) {
    router.back()
    return
  }
  void navigateTo('/')
}

onMounted(() => {
  timer = setInterval(() => {
    seconds.value -= 1
    if (seconds.value <= 0) {
      if (timer) clearInterval(timer)
      goBack()
    }
  }, 1000)
})

onBeforeUnmount(() => {
  if (timer) clearInterval(timer)
})

useHead({
  title: () => `${title.value} | 云端笔记`,
  meta: [{ name: 'robots', content: 'noindex, nofollow' }]
})
</script>

<template>
  <main class="error-shell">
    <section class="error-card" aria-live="polite">
      <p class="error-code">{{ error.statusCode || 500 }}</p>
      <div class="error-rule" aria-hidden="true" />
      <h1>{{ title }}</h1>
      <p>{{ description }}</p>
      <button type="button" class="error-back" @click="goBack">
        {{ seconds }} 秒后返回原页面
      </button>
    </section>
  </main>
</template>

<style>
.error-shell {
  display: grid;
  min-height: 100vh;
  place-items: center;
  overflow: hidden;
  padding: 1.5rem;
  background:
    radial-gradient(circle at 18% 15%, rgba(34, 211, 238, 0.12), transparent 32rem),
    radial-gradient(circle at 87% 82%, rgba(59, 130, 246, 0.1), transparent 30rem),
    #0b1220;
  color: #d9e5f4;
}

.error-card {
  width: min(100%, 32rem);
  padding: clamp(2rem, 7vw, 4rem);
  border: 1px solid rgba(103, 232, 249, 0.18);
  background: rgba(12, 24, 42, 0.8);
  box-shadow: 0 1.75rem 5rem rgba(0, 0, 0, 0.26);
  text-align: center;
}

.error-code {
  margin: 0;
  color: #67e8f9;
  font-family: var(--font-title, sans-serif);
  font-size: clamp(4.5rem, 18vw, 8rem);
  font-weight: 700;
  letter-spacing: -0.08em;
  line-height: 0.85;
}

.error-rule { width: 2.5rem; height: 2px; margin: 2rem auto 1.25rem; background: #22d3ee; }
.error-card h1 { margin: 0; color: #eff6ff; font-family: var(--font-title, sans-serif); font-size: 1.55rem; }
.error-card > p:not(.error-code) { margin: 0.75rem auto 1.8rem; color: #92a4bd; font-size: 0.9rem; line-height: 1.8; }
.error-back { border: 1px solid rgba(103, 232, 249, 0.45); background: transparent; color: #b9ecf7; cursor: pointer; padding: 0.65rem 1rem; font: inherit; font-size: 0.82rem; transition: background-color 160ms ease, border-color 160ms ease, color 160ms ease; }
.error-back:hover, .error-back:focus-visible { border-color: #67e8f9; background: rgba(34, 211, 238, 0.11); color: #fff; outline: none; }

html.theme-light .error-shell {
  background: radial-gradient(circle at 18% 15%, rgba(37, 99, 235, .11), transparent 32rem), radial-gradient(circle at 87% 82%, rgba(14, 165, 233, .08), transparent 30rem), #f5f7fb;
  color: #1e293b;
}
html.theme-light .error-card { border-color: #dbe4f0; background: rgba(255,255,255,.94); box-shadow: 0 1.75rem 5rem rgba(15,23,42,.1); }
html.theme-light .error-code { color: #2563eb; }
html.theme-light .error-rule { background-color: #2563eb; }
html.theme-light .error-card h1 { color: #1e293b; }
html.theme-light .error-card > p:not(.error-code) { color: #64748b; }
html.theme-light .error-back { border-color: #93c5fd; color: #1d4ed8; background: #fff; }
html.theme-light .error-back:hover, html.theme-light .error-back:focus-visible { border-color: #2563eb; background: #eff6ff; color: #1d4ed8; }
</style>
